import type {ExamModule as ModelExamModule, ModuleTopic} from '@cp/model';
import {ContentContainer} from '@coolcolduk/ui-mui';
import {Alert, Box} from '@mui/material';
import {useState} from 'react';
import {useNavigate} from 'react-router';
import {ExamModule, ModuleTopicInput, StandardSubject, SubjectLevel} from '@cp/model';
import ConfirmationDialog from '../../../components/ConfirmationDialog/ConfirmationDialog';
import {PrivateRouteProps} from '../../../config/routeType';
import useCreateExamModule from '../../../hooks/examModules/useCreateExamModule';
import useDeleteExamModule from '../../../hooks/examModules/useDeleteExamModule';
import useListExamModules from '../../../hooks/examModules/useListExamModules';
import useUpdateExamModule from '../../../hooks/examModules/useUpdateExamModule';
import mapModuleTopicToTopicString from '../../../utils/map/mapModuleTopicToTopicString';
import mapTopicStringToModuleTopic from '../../../utils/map/mapTopicStringToModuleTopic';
import LoadingPage from '../../other/LoadingPage';
import ExamModuleForm from './ExamModuleForm';
import ExamModuleHeader from './ExamModuleHeader';
import ExamModuleList from './ExamModuleList';

function mapTopicInputToModuleTopic(t: ModuleTopicInput): ModuleTopic {
  return {topic: t.topic, category: t.category, detail: t.detail ?? undefined};
}

interface AdminModuleFormData {
  name: string;
  level: SubjectLevel;
  subject: StandardSubject;
  topics: string[];
  examBoard: string;
  questionsPerTopic: number;
  requiredRole: string;
  attribution: string;
  author: string;
  source: string;
  license: string;
}

const initialFormData: AdminModuleFormData = {
  name: '',
  level: SubjectLevel.GCSE,
  subject: StandardSubject.MATHEMATICS,
  topics: [],
  examBoard: '',
  questionsPerTopic: 10,
  requiredRole: '',
  attribution: '',
  author: '',
  source: '',
  license: '',
};

export default function AdminModulePage({user: _user}: PrivateRouteProps) {
  const navigate = useNavigate();
  const {examModules, loading, error} = useListExamModules();
  const {createExamModule, loading: creating} = useCreateExamModule();
  const {updateExamModule, loading: updating} = useUpdateExamModule();
  const {deleteExamModule, loading: deleting} = useDeleteExamModule();
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [editingModule, setEditingModule] = useState<ExamModule | null>(null);
  const [moduleToDelete, setModuleToDelete] = useState<ExamModule | null>(null);
  const [formData, setFormData] = useState<AdminModuleFormData>(initialFormData);
  const [topicsText, setTopicsText] = useState('');
  const [alertMessage, setAlertMessage] = useState<{type: 'success' | 'error'; message: string} | null>(null);
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};

    if (!formData.name.trim()) {
      errors.name = 'Module name is required';
    }

    if (!topicsText.trim()) {
      errors.topics = 'At least one topic is required';
    } else {
      const topics = topicsText.split('\n').filter((topic) => topic.trim());
      if (topics.length === 0) {
        errors.topics = 'At least one topic is required';
      }
    }

    if (formData.questionsPerTopic < 1) {
      errors.questionsPerTopic = 'Questions per topic must be at least 1';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenDialog = (module?: ExamModule) => {
    if (module) {
      setEditingModule(module);
      setFormData({
        name: module.name,
        level: module.level,
        subject: module.subject,
        topics: module.topics?.map((t) => t.topic) || [],
        examBoard: module.examBoard || '',
        questionsPerTopic: module.questionsPerTopic || 10,
        requiredRole: module.requiredRole || '',
        attribution: module.attribution || '',
        author: module.author || '',
        source: module.source || '',
        license: module.license || '',
      });
      setTopicsText(module.topics?.map((t) => mapModuleTopicToTopicString(t, {isFull: true})).join('\n') || '');
    } else {
      setEditingModule(null);
      setFormData(initialFormData);
      setTopicsText('');
    }
    setOpenDialog(true);
  };

  const handleOpenViewDialog = (module: ExamModule) => {
    navigate(`/admin/module/${module.id}`);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingModule(null);
    setFormData(initialFormData);
    setTopicsText('');
    setAlertMessage(null);
    setFormErrors({});
  };

  const handleSubmit = async (data: AdminModuleFormData, topicString: string) => {
    // Clear previous errors
    setFormErrors({});

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    try {
      const topicStrings = topicString.split('\n').filter((topic) => topic.trim());
      const topicInputs: ModuleTopicInput[] = topicStrings.map(mapTopicStringToModuleTopic) as ModuleTopicInput[];
      const topics = topicInputs.map(mapTopicInputToModuleTopic);

      if (editingModule) {
        const payload: ModelExamModule = {
          ...editingModule,
          id: editingModule.id,
          name: data.name,
          requiredRole: data.requiredRole ?? '',
          topics,
          questionsPerTopic: data.questionsPerTopic,
          attribution: data.attribution ?? undefined,
          author: data.author ?? undefined,
          source: data.source ?? undefined,
          license: data.license ?? undefined,
          examBoard: editingModule.examBoard ?? undefined,
          disableStudy: editingModule.disableStudy ?? undefined,
          disableQuestion: editingModule.disableQuestion ?? undefined,
        };
        await updateExamModule(payload);
        setAlertMessage({type: 'success', message: 'Exam module updated successfully!'});
      } else {
        await createExamModule({
          name: data.name,
          level: data.level,
          subject: data.subject,
          topics,
          requiredRole: data.requiredRole ?? '',
          examBoard: data.examBoard || undefined,
          questionsPerTopic: data.questionsPerTopic,
          attribution: data.attribution ?? undefined,
          author: data.author ?? undefined,
          source: data.source ?? undefined,
          license: data.license ?? undefined,
        });
        setAlertMessage({type: 'success', message: 'Exam module created successfully!'});
      }

      setTimeout(() => {
        handleCloseDialog();
      }, 1500);
    } catch (err: any) {
      console.error('Error submitting exam module:', err);

      // Extract error message from GraphQL error or use fallback
      let errorMessage = 'An error occurred. Please try again.';

      if (err?.graphQLErrors && err.graphQLErrors.length > 0) {
        errorMessage = err.graphQLErrors[0].message;
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (err?.networkError) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      setAlertMessage({type: 'error', message: errorMessage});
    }
  };

  const handleDeleteClick = (module: ExamModule) => {
    setModuleToDelete(module);
    setOpenDeleteDialog(true);
  };

  const handleDeleteClose = () => {
    setOpenDeleteDialog(false);
    setModuleToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!moduleToDelete) return;

    try {
      await deleteExamModule(moduleToDelete.id);
      setAlertMessage({type: 'success', message: 'Exam module deleted successfully!'});
      handleDeleteClose();
    } catch (err: any) {
      console.error('Error deleting exam module:', err);

      // Extract error message from GraphQL error or use fallback
      let errorMessage = 'An error occurred while deleting.';

      if (err?.graphQLErrors && err.graphQLErrors.length > 0) {
        errorMessage = err.graphQLErrors[0].message;
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (err?.networkError) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }

      setAlertMessage({type: 'error', message: errorMessage});
    }
  };

  const handleFormDataChange = (data: AdminModuleFormData) => {
    setFormData(data);
  };

  const handleTopicsTextChange = (text: string) => {
    setTopicsText(text);
  };

  const handleClearError = (field: string) => {
    setFormErrors((prev) => ({...prev, [field]: ''}));
  };

  if (loading) {
    return <LoadingPage minusAppBar />;
  }

  if (error) {
    return (
      <Box sx={{p: 3}}>
        <Alert severity="error">Error loading exam modules: {error.message}</Alert>
      </Box>
    );
  }

  return (
    <ContentContainer data-testid="module-management-page">
      <ExamModuleHeader handleOpenDialog={handleOpenDialog} />

      {alertMessage && (
        <Alert severity={alertMessage.type} sx={{mb: 2}}>
          {alertMessage.message}
        </Alert>
      )}

      <ExamModuleList
        examModules={examModules}
        onEdit={handleOpenDialog}
        onDelete={handleDeleteClick}
        onView={handleOpenViewDialog}
      />

      <ExamModuleForm
        open={openDialog}
        examModule={editingModule}
        formData={formData}
        topicsText={topicsText}
        formErrors={formErrors}
        creating={creating}
        updating={updating}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        onFormDataChange={handleFormDataChange}
        onTopicsTextChange={handleTopicsTextChange}
        onClearError={handleClearError}
      />

      <ConfirmationDialog
        open={openDeleteDialog}
        onClose={handleDeleteClose}
        title="Delete Exam Module"
        content={
          <>
            Are you sure you want to delete <strong>{moduleToDelete?.name}</strong>? This action cannot be undone.
          </>
        }
        confirmButtonLabel="Delete"
        cancelButtonLabel="Cancel"
        confirmButtonColor="error"
        handleConfirm={handleDeleteConfirm}
        handleCancel={handleDeleteClose}
        loading={deleting}
      />
    </ContentContainer>
  );
}
